import "antd/dist/reset.css";
import { Spin } from "antd";
import { Navigate, Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ModuleProvider } from "./context/ModuleContext";
import Login from "./pages/auth/Login";
import Signup from "./pages/auth/Signup";
import AdminDashboard from "./pages/dashboard/AdminDashboard";
import DoctorDashboard from "./pages/dashboard/DoctorDashboard";
import BillingDashboard from "./pages/dashboard/BillingDashboard";
import EMSDashboard from "./pages/dashboard/EMSDashboard";
import OperationsManagerDashboard from "./pages/dashboard/OperationsManagerDashboard";
import PartnerOrgDashboard from "./pages/dashboard/PartnerOrgDashboard";
import PatientDashboard from "./pages/dashboard/PatientDashboard";
import PharmacyDashboard from "./pages/dashboard/PharmacyDashboard";
import OphthalmologyDashboard from "./pages/dashboard/OphthalmologyDashboard";
import ShareholderDashboard from "./pages/dashboard/ShareholderDashboard";
import { getDashboardRoute } from "./utils/roleUtils";

// Maps each role to its authorised dashboard path.
const ROLE_DASHBOARD = {
  SYSTEM_ADMIN:       "/dashboard/admin",
  DOCTOR:             "/dashboard/doctor",
  PATIENT:            "/dashboard/patient",
  PHARMACIST:         "/dashboard/pharmacy",
  OPHTHALMOLOGIST:    "/dashboard/ophthalmology",
  OPTICIAN:           "/dashboard/ophthalmology",
  EMS_DISPATCHER:     "/dashboard/ems",
  EMS_DRIVER:         "/dashboard/ems",
  BILLING_STAFF:      "/dashboard/billing",
  SHAREHOLDER:        "/dashboard/shareholder",
  OPERATIONS_MANAGER:         "/dashboard/operations",
  ORGANISATION_ADMINISTRATOR: "/dashboard/operations",
  PARTNER_ORG:                "/dashboard/partner",
};

function LoadingScreen() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
      <Spin size="large" description="Loading…" />
    </div>
  );
}

/**
 * Requires authentication AND that the current user's role is in allowedRoles.
 * If authenticated but wrong role, redirects to their own dashboard.
 */
function RoleGuard({ allowedRoles, children }) {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!allowedRoles.includes(user?.role)) {
    const target = ROLE_DASHBOARD[user?.role] ?? getDashboardRoute(user?.role);
    return <Navigate to={target} replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/login"  element={<Login />} />
      <Route path="/signup" element={<Signup />} />

      <Route path="/dashboard/admin" element={
        <RoleGuard allowedRoles={["SYSTEM_ADMIN"]}>
          <AdminDashboard />
        </RoleGuard>
      } />

      <Route path="/dashboard/doctor" element={
        <RoleGuard allowedRoles={["DOCTOR"]}>
          <DoctorDashboard />
        </RoleGuard>
      } />

      <Route path="/dashboard/patient" element={
        <RoleGuard allowedRoles={["PATIENT"]}>
          <PatientDashboard />
        </RoleGuard>
      } />

      <Route path="/dashboard/pharmacy" element={
        <RoleGuard allowedRoles={["PHARMACIST"]}>
          <PharmacyDashboard />
        </RoleGuard>
      } />

      <Route path="/dashboard/ems" element={
        <RoleGuard allowedRoles={["EMS_DISPATCHER", "EMS_DRIVER"]}>
          <EMSDashboard />
        </RoleGuard>
      } />

      <Route path="/dashboard/ophthalmology" element={
        <RoleGuard allowedRoles={["OPHTHALMOLOGIST", "OPTICIAN"]}>
          <OphthalmologyDashboard />
        </RoleGuard>
      } />

      <Route path="/dashboard/billing" element={
        <RoleGuard allowedRoles={["BILLING_STAFF"]}>
          <BillingDashboard />
        </RoleGuard>
      } />

      <Route path="/dashboard/shareholder" element={
        <RoleGuard allowedRoles={["SHAREHOLDER"]}>
          <ShareholderDashboard />
        </RoleGuard>
      } />

      <Route path="/dashboard/operations" element={
        <RoleGuard allowedRoles={["OPERATIONS_MANAGER", "ORGANISATION_ADMINISTRATOR"]}>
          <OperationsManagerDashboard />
        </RoleGuard>
      } />

      <Route path="/dashboard/partner" element={
        <RoleGuard allowedRoles={["PARTNER_ORG"]}>
          <PartnerOrgDashboard />
        </RoleGuard>
      } />

      {/* Kiosk - temporarily disabled */}
      {/* <Route path="/dashboard/kiosk" element={
        <RoleGuard allowedRoles={["SYSTEM_ADMIN"]}>
          <KioskDashboard />
        </RoleGuard>
      } /> */}
    </Routes>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <ModuleProvider>
          <AppRoutes />
        </ModuleProvider>
      </AuthProvider>
    </Router>
  );
}
